import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { buildApiUrl, buildAssetUrl, getPlaceholderImage } from '../../../config/constants';

interface Note {
  id: number;
  bookId: number;
  bookTitle: string;
  content: string;
  pageNumber?: number;
  color?: string;
  createdAt: string;
  book?: {
    id: number;
    title: string;
    author: string;
    coverImage?: string;
  };
}

const NotesTab: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { token } = useAuth();

  const fetchNotes = async () => {
    if (!token) {
      setError('Nu sunteți autentificat');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/annotations/my-notes'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Nu s-au putut încărca notițele');
      }

      const data = await response.json();
      setNotes(data);
    } catch (err) {
      console.error('Eroare la încărcarea notițelor:', err);
      setError('Nu s-au putut încărca notițele');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [token]);

  const handleDeleteNote = async (noteId: number) => {
    if (!token) return;

    if (!window.confirm('Sigur doriți să ștergeți această notă?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/annotations/note/${noteId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotes(prev => prev.filter(note => note.id !== noteId));
      } else {
        alert('Nu s-a putut șterge nota');
      }
    } catch (err) {
      console.error('Eroare la ștergerea notei:', err);
      alert('Nu s-a putut șterge nota');
    }
  };

  const getBookCoverImage = (book: Note['book']) => {
    if (book?.coverImage) {
      return buildAssetUrl(book.coverImage);
    }
    return getPlaceholderImage(book?.title || 'Carte', 150, 200);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchNotes}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Încearcă din nou
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Notițele Mele</h2>
        <span className="text-sm text-gray-500">
          {notes.length} {notes.length === 1 ? 'notă' : 'note'}
        </span>
      </div>
      
      {notes.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nu ai nicio notă</h3>
          <p className="mt-1 text-sm text-gray-500">
            Adaugă note în timpul citirii cărților din biblioteca noastră.
          </p>
          <Link
            to="/books"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Explorează Cărțile
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {notes.map((note) => (
            <div key={note.id} className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:px-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {note.book && (
                      <img
                        src={getBookCoverImage(note.book)}
                        alt={note.book.title}
                        className="w-16 h-20 object-cover rounded"
                      />
                    )}
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {note.bookTitle}
                      </h3>
                      {note.book?.author && (
                        <p className="text-sm text-gray-600">de {note.book.author}</p>
                      )}
                      <div className="mt-1 flex items-center text-xs text-gray-500 space-x-2">
                        <span>
                          {new Date(note.createdAt).toLocaleDateString('ro-RO', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {note.pageNumber && (
                          <>
                            <span>•</span>
                            <span>Pagina {note.pageNumber}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/books/${note.bookId}`}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Vezi Cartea
                    </Link>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Șterge
                    </button>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200">
                <div className="px-4 py-5 sm:p-6">
                  <div 
                    className="text-sm text-gray-900 whitespace-pre-line p-3 rounded-md border-l-4"
                    style={{ 
                      borderLeftColor: note.color || '#FFFF00',
                      backgroundColor: `${note.color || '#FFFF00'}20`
                    }}
                  >
                    {note.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesTab; 
